import type { SupabaseClient } from "@supabase/supabase-js";

import {
  REPORT_SITE_SELECTION_MAX,
  resolveReportDateRange,
  type ReportColumnKey,
  type ReportDateSelection,
  type ReportResolvedDateRange,
  type ReportScanLifecycleState,
  type ReportScanMethod,
  type ReportScanSort,
} from "./report-scan-filters";

// Live Activity read model — the safe hosted public.supervisor_scan_list view (management-scoped
// by RLS; guards get zero rows). We select * and map defensively so an unexpected/missing column
// degrades to a placeholder rather than a crash. Column shape mirrors apps/web's scan-list
// view-model. Read-only.

export interface ScanRow {
  clientEventId: string;
  /**
   * Hosted `scan_id` (checkpoint_scans.id). Present after 20260809120000; empty string when the
   * column is absent so CAD can still degrade to name/time matching.
   */
  scanId: string;
  /**
   * True when the scan row itself has lat/lng. False means Activity may still map to a
   * checkpoint expected pin via scan_points. Null when the column is not served yet.
   */
  hasScanGps: boolean | null;
  checkpointName: string | null;
  siteName: string | null;
  routeName: string | null;
  guardName: string | null;
  /** Redacted officer key and assignment key already projected by the management view. */
  guardRef?: string | null;
  routeAssignmentId?: string | null;
  scanMethod: string;
  lifecycleState: string;
  serverReceivedAt: string;
  deviceTime: string | null;
  gpsAccuracyMeters: number | null;
  gpsPermission: string | null;
  validationNotes: string | null;
  /**
   * Forward-compatible: populated once server column `presence_kind` exists.
   * Values: "adhoc" | "standard" | null.  Undefined when column is absent.
   */
  presenceKind?: string | null;
  /**
   * Forward-compatible: populated once server column `guard_note` exists.
   * Undefined when column is absent; null when present but empty.
   */
  guardNote?: string | null;
  /**
   * Review-required flag surfaced by `supervisor_scan_list`. Sourced from the
   * existing `verification_review_required` column; a legacy `review_required`
   * alias is also accepted. Undefined when neither key is present.
   */
  reviewRequired?: boolean | null;
}

/**
 * Returns true when this scan is ad-hoc / manual presence rather than an
 * NFC/QR token verification.  Resolution order (forward-compatible):
 *  1. `presenceKind` in {adhoc, manual} when the server column is present
 *  2. `scanMethod === "MANUAL"` when presenceKind is absent/null
 *
 * Note: presence_kind=`manual` (optional note on expected checkpoint) must
 * still surface badges + guard notes. Treating only `adhoc` as presence was
 * hiding Manual notes after PRD-001H started sending an explicit kind.
 */
export function isAdHocPresence(row: Pick<ScanRow, "presenceKind" | "scanMethod">): boolean {
  if (row.presenceKind !== undefined && row.presenceKind !== null) {
    const kind = row.presenceKind.toLowerCase();
    return kind === "adhoc" || kind === "manual";
  }
  return row.scanMethod.toUpperCase() === "MANUAL";
}

/** Human label for presence kind (used in badge). */
export function presenceKindLabel(
  row: Pick<ScanRow, "presenceKind" | "scanMethod">,
): string | null {
  if (!isAdHocPresence(row)) return null;
  if (row.presenceKind?.toLowerCase() === "adhoc") return "Ad-hoc";
  return "Manual presence";
}

/**
 * Title for free-form presence: the guard deliberately checked in at their current
 * location with no checkpoint pin, and the server required a note for it.
 *
 * Mirrors the field label mobile shows at capture time (`FREEFORM_PRESENCE_LABEL` in
 * packages/guard-core/src/freeform-presence.ts and apps/mobile/src/scan/freeform-presence.ts).
 * Duplicated on purpose: the console carries no runtime dependency on guard-core.
 * scan-list.test.ts pins this constant to the guard-core source so the two cannot drift.
 */
export const FREEFORM_PRESENCE_LABEL = "Here (no pin)";

/**
 * Title for a scan whose checkpoint genuinely could not be resolved: a token capture
 * that should have named a checkpoint and did not. This is a data problem worth chasing,
 * which is exactly why free-form presence must NOT borrow the wording.
 */
export const UNRESOLVED_CHECKPOINT_LABEL = "Unresolved checkpoint";

/** The fields any scan-shaped row needs to title itself. `presenceKind` may be absent. */
export interface CheckpointLabelSource {
  readonly checkpointName: string | null;
  readonly presenceKind?: string | null;
  readonly scanMethod: string | null;
}

/**
 * Single source of truth for the checkpoint title across Activity, Reports, and the CAD
 * check-in layer, so those surfaces cannot disagree about the same scan.
 *
 *  1. A resolved checkpoint name always wins.
 *  2. Free-form / manual presence without a pin reads "Here (no pin)". Honest, because
 *     nothing failed to resolve; the guard chose to record presence off-pin.
 *  3. Anything else missing a name is a real resolution failure: "Unresolved checkpoint".
 */
export function checkpointDisplayLabel(row: CheckpointLabelSource): string {
  const name = row.checkpointName;
  if (name !== null && name.trim().length > 0) {
    return name;
  }
  // presenceKind stays absent rather than explicitly undefined so isAdHocPresence keeps its
  // "column not served yet" branch (exactOptionalPropertyTypes is on).
  const method = row.scanMethod ?? "";
  const presence = isAdHocPresence(
    row.presenceKind === undefined
      ? { scanMethod: method }
      : { presenceKind: row.presenceKind, scanMethod: method },
  );
  return presence ? FREEFORM_PRESENCE_LABEL : UNRESOLVED_CHECKPOINT_LABEL;
}

function asString(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

function asNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string" && value.trim().length > 0) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

/** Map a raw supervisor_scan_list row (snake_case) to a typed ScanRow. Defensive. */
export function mapScanRow(raw: Record<string, unknown>): ScanRow {
  const row: ScanRow = {
    clientEventId: asString(raw.client_event_id) ?? "",
    scanId: asString(raw.scan_id) ?? "",
    hasScanGps: typeof raw.has_scan_gps === "boolean" ? raw.has_scan_gps : null,
    checkpointName: asString(raw.checkpoint_name),
    siteName: asString(raw.site_name),
    routeName: asString(raw.route_name),
    guardName: asString(raw.guard_name),
    guardRef: asString(raw.guard_ref),
    routeAssignmentId: asString(raw.route_assignment_id),
    scanMethod: asString(raw.scan_method) ?? "—",
    lifecycleState: asString(raw.lifecycle_state) ?? "captured",
    serverReceivedAt: asString(raw.server_received_at) ?? "",
    deviceTime: asString(raw.device_time),
    gpsAccuracyMeters: asNumber(raw.gps_accuracy_meters),
    gpsPermission: asString(raw.gps_permission),
    validationNotes: asString(raw.validation_notes),
  };
  // Forward-compatible optional columns — only populate if key is present in the raw row
  // so consumers can detect "column not yet available" vs "column is null".
  if ("presence_kind" in raw) {
    row.presenceKind = asString(raw.presence_kind);
  }
  if ("guard_note" in raw) {
    row.guardNote = asString(raw.guard_note);
  }
  // `verification_review_required` is the canonical column on supervisor_scan_list
  // (boolean, always present for real rows); `review_required` is accepted as a
  // forward-compat alias in case a future view renames it.
  if ("verification_review_required" in raw) {
    row.reviewRequired =
      typeof raw.verification_review_required === "boolean" ? raw.verification_review_required : null;
  } else if ("review_required" in raw) {
    row.reviewRequired = typeof raw.review_required === "boolean" ? raw.review_required : null;
  }
  return row;
}

/** Human GPS quality label (never raw coordinates). */
export function gpsQualityLabel(row: Pick<ScanRow, "gpsPermission" | "gpsAccuracyMeters">): string {
  if (row.gpsPermission === "denied") return "GPS denied";
  if (row.gpsPermission === "unavailable") return "GPS unavailable";
  if (row.gpsAccuracyMeters !== null) {
    const rounded = Math.round(row.gpsAccuracyMeters);
    return `±${rounded}m`;
  }
  return "GPS unknown";
}

const LIVE_ACTIVITY_LIMIT = 50;

export interface ScanListFilters {
  siteName?: string | null;
  guardName?: string | null;
  /** Inclusive lower bound on `server_received_at` (ISO). Applied before the window limit. */
  receivedFromIso?: string | null;
  /** Inclusive upper bound on `server_received_at` (ISO). Applied before the window limit. */
  receivedToIso?: string | null;
}

export const REPORT_SCAN_LIMIT = 500;

export type ReportScanRead =
  | {
      readonly kind: "ok";
      readonly rows: ScanRow[];
      readonly capped: boolean;
      readonly range: ReportResolvedDateRange;
      readonly sort: ReportScanSort;
    }
  | {
      readonly kind: "unavailable";
      readonly reason: "self_serve_floor";
      readonly floorDay: string;
    }
  | {
      readonly kind: "unavailable";
      readonly reason: "too_many_sites";
      readonly selected: number;
      readonly max: number;
    }
  | {
      readonly kind: "unavailable";
      readonly reason: "unavailable";
    };

export interface ReportScanFilters extends ScanListFilters {
  readonly dateSelection: ReportDateSelection;
  readonly timeZone: string;
  readonly now?: Date;
  /** Multi-select sites; empty means all. Cap is REPORT_SITE_SELECTION_MAX. */
  readonly siteNames?: readonly string[];
  readonly routeName?: string | null;
  readonly checkpointName?: string | null;
  readonly methods?: readonly ReportScanMethod[];
  readonly presenceAdhocOnly?: boolean;
  readonly lifecycleStates?: readonly ReportScanLifecycleState[];
  readonly reviewRequiredOnly?: boolean;
  /** Client-only reorder key returned on ok reads; host window stays receipt order. */
  readonly sort?: ReportScanSort;
}

/** Fetch the latest scans for an account. Throws on read error (caller surfaces the error state). */
export async function fetchScanList(
  client: SupabaseClient,
  accountId: string,
  limit: number = LIVE_ACTIVITY_LIMIT,
  filters: ScanListFilters = {},
): Promise<ScanRow[]> {
  let query = client
    .from("supervisor_scan_list")
    .select("*")
    .eq("account_id", accountId);
  const siteName = filters.siteName?.trim() ?? "";
  const guardName = filters.guardName?.trim() ?? "";
  const receivedFromIso = filters.receivedFromIso?.trim() ?? "";
  const receivedToIso = filters.receivedToIso?.trim() ?? "";
  if (siteName.length > 0) {
    query = query.eq("site_name", siteName);
  }
  if (guardName.length > 0) {
    query = query.eq("guard_name", guardName);
  }
  if (receivedFromIso.length > 0) {
    query = query.gte("server_received_at", receivedFromIso);
  }
  if (receivedToIso.length > 0) {
    query = query.lte("server_received_at", receivedToIso);
  }
  const { data, error } = await query
    .order("server_received_at", { ascending: false })
    .limit(limit);
  if (error !== null) {
    throw new Error("Could not load live activity.");
  }
  if (!Array.isArray(data)) {
    return [];
  }
  return data.map((row) => mapScanRow(row as Record<string, unknown>));
}

/**
 * Reports-only bounded reader. The date resolver refuses wholly old or invalid requests
 * before `client.from` can run, then every filter is applied before the 500-row window.
 */
export async function fetchReportScanList(
  client: SupabaseClient,
  accountId: string,
  filters: ReportScanFilters,
): Promise<ReportScanRead> {
  const resolution = resolveReportDateRange(
    filters.dateSelection,
    filters.timeZone,
    filters.now ?? new Date(),
  );
  if (resolution.kind === "unavailable") {
    return resolution;
  }
  if (resolution.kind === "invalid") {
    throw new Error("Invalid report date range.");
  }

  const siteNames = normalizeUniqueLabels(filters.siteNames);
  if (siteNames.length > REPORT_SITE_SELECTION_MAX) {
    return {
      kind: "unavailable",
      reason: "too_many_sites",
      selected: siteNames.length,
      max: REPORT_SITE_SELECTION_MAX,
    };
  }

  const legacySite = filters.siteName?.trim() ?? "";
  const effectiveSites =
    siteNames.length > 0 ? siteNames : legacySite.length > 0 ? [legacySite] : [];
  if (effectiveSites.length > REPORT_SITE_SELECTION_MAX) {
    return {
      kind: "unavailable",
      reason: "too_many_sites",
      selected: effectiveSites.length,
      max: REPORT_SITE_SELECTION_MAX,
    };
  }

  let query = client
    .from("supervisor_scan_list")
    .select("*")
    .eq("account_id", accountId);

  if (effectiveSites.length === 1) {
    query = query.eq("site_name", effectiveSites[0]!);
  } else if (effectiveSites.length > 1) {
    query = query.in("site_name", [...effectiveSites]);
  }

  const guardName = filters.guardName?.trim() ?? "";
  if (guardName.length > 0) {
    query = query.eq("guard_name", guardName);
  }

  const routeName = filters.routeName?.trim() ?? "";
  if (routeName.length > 0) {
    query = query.eq("route_name", routeName);
  }

  const checkpointName = filters.checkpointName?.trim() ?? "";
  if (checkpointName.length > 0) {
    query = query.eq("checkpoint_name", checkpointName);
  }

  const methods = uniqueEnumValues(filters.methods);
  if (methods.length === 1) {
    query = query.eq("scan_method", methods[0]!);
  } else if (methods.length > 1) {
    query = query.in("scan_method", [...methods]);
  }

  if (filters.presenceAdhocOnly === true) {
    query = query.eq("presence_kind", "adhoc");
  }

  const lifecycleStates = uniqueEnumValues(filters.lifecycleStates);
  if (lifecycleStates.length === 1) {
    query = query.eq("lifecycle_state", lifecycleStates[0]!);
  } else if (lifecycleStates.length > 1) {
    query = query.in("lifecycle_state", [...lifecycleStates]);
  }

  if (filters.reviewRequiredOnly === true) {
    query = query.eq("verification_review_required", true);
  }

  const { data, error } = await query
    .gte("server_received_at", resolution.range.fromIso)
    .lte("server_received_at", resolution.range.toIso)
    .order("server_received_at", { ascending: false })
    .limit(REPORT_SCAN_LIMIT);
  if (error !== null) {
    throw new Error("Could not load the scan report.");
  }
  if (!Array.isArray(data)) return { kind: "unavailable", reason: "unavailable" };
  const rows = data.map((row) => mapScanRow(row as Record<string, unknown>));
  const sort = filters.sort ?? { column: "captured" as const, direction: "desc" as const };
  return {
    kind: "ok",
    rows,
    capped: rows.length >= REPORT_SCAN_LIMIT,
    range: resolution.range,
    sort,
  };
}

function normalizeUniqueLabels(values: readonly string[] | undefined): string[] {
  if (values === undefined || values.length === 0) return [];
  const out: string[] = [];
  const seen = new Set<string>();
  for (const raw of values) {
    const label = raw.trim();
    if (label.length === 0 || seen.has(label)) continue;
    seen.add(label);
    out.push(label);
  }
  return out;
}

function uniqueEnumValues<T extends string>(values: readonly T[] | undefined): T[] {
  if (values === undefined || values.length === 0) return [];
  return [...new Set(values)];
}

/** Reorder already-fetched report rows inside the host window (O-5 / D-14). */
export function sortReportScanRows(
  rows: readonly ScanRow[],
  sort: ReportScanSort,
): ScanRow[] {
  const direction = sort.direction === "asc" ? 1 : -1;
  return [...rows].sort((left, right) => {
    const cmp = compareReportScanColumn(left, right, sort.column);
    if (cmp !== 0) return cmp * direction;
    // Stable tie: newer server receipt first, then client event id.
    const timeCmp = right.serverReceivedAt.localeCompare(left.serverReceivedAt);
    if (timeCmp !== 0) return timeCmp;
    return left.clientEventId.localeCompare(right.clientEventId);
  });
}

function compareReportScanColumn(
  left: ScanRow,
  right: ScanRow,
  column: ReportColumnKey,
): number {
  switch (column) {
    case "captured":
      return (left.deviceTime ?? "").localeCompare(right.deviceTime ?? "");
    case "received":
      return left.serverReceivedAt.localeCompare(right.serverReceivedAt);
    case "checkpoint":
      return checkpointDisplayLabel(left).localeCompare(checkpointDisplayLabel(right));
    case "site":
      return (left.siteName ?? "").localeCompare(right.siteName ?? "");
    case "route":
      return (left.routeName ?? "").localeCompare(right.routeName ?? "");
    case "guard":
      return (left.guardName ?? "").localeCompare(right.guardName ?? "");
    case "method":
      return left.scanMethod.localeCompare(right.scanMethod);
    case "status":
      return left.lifecycleState.localeCompare(right.lifecycleState);
    case "gps":
      return gpsQualityLabel(left).localeCompare(gpsQualityLabel(right));
    case "validation_notes":
      return (left.validationNotes ?? "").localeCompare(right.validationNotes ?? "");
  }
}
